/**
 * XYON Blog Library — Interactivity
 * Handles: topic filter, TOC generation, scroll spy, glossary scanning,
 *          copy link, changelog toggle, mobile TOC toggle.
 *
 * No dependencies. Deferred via <script defer>.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
   * 1. TWO-AXIS FILTER + STATUS BAR + SORT
   *
   * Stage chips (data-journey) show/hide entire funnel sections.
   * Topic pills (data-filter-topic) show/hide individual cards.
   * Sort dropdown (data-sort) reorders cards within each visible grid.
   *
   * All three pieces write to the URL — ?stage=, ?topic=, ?sort= —
   * via history.replaceState so deep links carry state and reload
   * preserves it. Legacy ?funnel= is honoured as a backwards-compat
   * alias for ?stage=.
   *
   * The status bar (data-status-*) reflects the current filter combo
   * and total visible-card count.
   *
   * No fetch — all articles are server-rendered; JS shows/hides/reorders.
   * ───────────────────────────────────────────── */
  function initTopicFilter() {
    var journeyTabs  = document.querySelectorAll('[data-journey]');
    var topicPills   = document.querySelectorAll('[data-filter-topic]');
    var sortSelect   = document.querySelector('[data-sort]');
    var emptyStateEl = document.getElementById('lib-empty-state');

    if (!journeyTabs.length && !topicPills.length) return;

    var journeySections = document.querySelectorAll('[data-funnel-section]');
    var activeJourney   = 'all';
    var activeTopic     = 'all';
    var activeSort      = 'recommended';

    /* Status bar elements (optional — section may render without them) */
    var statusCount     = document.querySelector('[data-status-count]');
    var statusStage     = document.querySelector('[data-status-stage]');
    var statusTopic     = document.querySelector('[data-status-topic]');
    var statusSepTopic  = document.querySelector('[data-status-sep-topic]');

    function getStageLabel(value) {
      var chip = document.querySelector('.lib-stage-chip[data-journey="' + value + '"]');
      if (!chip) return 'All guides';
      var label = chip.querySelector('.lib-stage-chip__label');
      return (label ? label.textContent : chip.textContent).trim();
    }

    function getTopicLabel(value) {
      if (value === 'all') return 'All topics';
      var pill = document.querySelector('.lib-pill[data-filter-topic="' + value + '"]');
      if (!pill) return value;
      return pill.textContent.trim();
    }

    /* Update the status bar (count · stage · topic) */
    function updateStatus() {
      if (statusCount) {
        var visible = 0;
        journeySections.forEach(function (sec) {
          if (sec.style.display === 'none') return;
          var cards = sec.querySelectorAll('.lib-card');
          cards.forEach(function (c) {
            if (c.style.display !== 'none') visible += 1;
          });
        });
        statusCount.textContent = visible + (visible === 1 ? ' guide' : ' guides');
      }
      if (statusStage) statusStage.textContent = getStageLabel(activeJourney);
      if (statusTopic) {
        var hide = activeTopic === 'all';
        statusTopic.textContent = getTopicLabel(activeTopic);
        statusTopic.classList.toggle('lib-status-topic--hidden', hide);
        if (statusSepTopic) statusSepTopic.classList.toggle('lib-status-sep--hidden', hide);
      }
    }

    /* ── Per-section fold ── Default reveal is FOLD_LIMIT cards; the rest
       sit behind a "Show N more guides" button injected per section. Runs
       after sort + filter so it always operates on the active order +
       result set. Expand state is sticky per section (stage handle) — it
       resets when the stage selection changes (because the section's
       visibility resets in applyFilters too). */
    var FOLD_LIMIT = 6;
    var sectionExpanded = {}; // { stageHandle: true } once expanded

    function getExpandButton(grid) {
      var row = grid.nextElementSibling;
      if (row && row.classList && row.classList.contains('lib-section-expand-row')) {
        return row.querySelector('[data-section-expand]');
      }
      // Inject the row + button if not present
      var wrapper = document.createElement('div');
      wrapper.className = 'lib-section-expand-row';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lib-section-expand';
      btn.setAttribute('data-section-expand', '');
      btn.innerHTML = '<span class="lib-section-expand__label"></span>' +
        '<svg class="lib-section-expand__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';
      wrapper.appendChild(btn);
      grid.parentNode.insertBefore(wrapper, grid.nextSibling);
      btn.addEventListener('click', function () {
        var stage = grid.closest('[data-funnel-section]').getAttribute('data-funnel-section');
        sectionExpanded[stage] = true;
        applyFold();
      });
      return btn;
    }

    function applyFold() {
      // Mobile already presents the grid as a horizontal scroll shelf —
      // swiping is the natural fold mechanism, so the "Show N more" pill
      // is redundant noise. Skip the fold entirely below 768px.
      var isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (isMobile) {
        document.querySelectorAll('.lib-card.is-folded').forEach(function (c) {
          c.classList.remove('is-folded');
        });
        document.querySelectorAll('.lib-section-expand-row').forEach(function (row) {
          row.setAttribute('hidden', '');
        });
        return;
      }
      // Make sure any rows that got hidden on mobile come back on desktop
      document.querySelectorAll('.lib-section-expand-row').forEach(function (row) {
        row.removeAttribute('hidden');
      });

      journeySections.forEach(function (sec) {
        var stage = sec.getAttribute('data-funnel-section');
        if (stage === 'general_awareness') return;

        var grid = sec.querySelector('[data-journey-grid]');
        if (!grid) return;

        var isExpanded = !!sectionExpanded[stage];

        // Cards that survived the filter pass (their inline display is '')
        var filterPassed = Array.from(grid.querySelectorAll('.lib-card')).filter(function (c) {
          return c.style.display !== 'none';
        });

        var hiddenByFold = 0;
        filterPassed.forEach(function (card, idx) {
          if (!isExpanded && idx >= FOLD_LIMIT) {
            card.classList.add('is-folded');
            hiddenByFold += 1;
          } else {
            card.classList.remove('is-folded');
          }
        });

        var btn = getExpandButton(grid);
        var label = btn.querySelector('.lib-section-expand__label');
        if (hiddenByFold > 0) {
          if (label) label.textContent = 'Show ' + hiddenByFold + ' more guide' + (hiddenByFold === 1 ? '' : 's');
          btn.removeAttribute('hidden');
        } else {
          btn.setAttribute('hidden', '');
        }
      });
    }

    /* Reorder cards within each grid based on activeSort */
    function applySort() {
      var grids = document.querySelectorAll('[data-journey-grid], [data-general-grid]');
      grids.forEach(function (grid) {
        var cards = Array.from(grid.querySelectorAll('.lib-card'));
        if (cards.length < 2) return;

        cards.sort(function (a, b) {
          if (activeSort === 'shortest') {
            var ar = parseInt(a.getAttribute('data-read-time') || '0', 10);
            var br = parseInt(b.getAttribute('data-read-time') || '0', 10);
            return ar - br;
          }

          if (activeSort === 'recommended') {
            // Editor's-picks rank ahead of regular cards; within each tier,
            // tie-break on published_at desc.
            var ap = a.getAttribute('data-editors-pick') === 'true' ? 1 : 0;
            var bp = b.getAttribute('data-editors-pick') === 'true' ? 1 : 0;
            if (ap !== bp) return bp - ap;
          }

          // 'recent' (and recommended-within-tier) — published_at desc.
          var ad = a.getAttribute('data-published-at') || '';
          var bd = b.getAttribute('data-published-at') || '';
          if (ad === bd) return 0;
          return ad < bd ? 1 : -1;
        });

        cards.forEach(function (c) { grid.appendChild(c); });
      });
    }

    /* Normalize a string for case-insensitive substring matching */
    function normalize(str) {
      return (str || '').toLowerCase();
    }

    /* Scope topic pills to the active stage — the entire pill row hides
       when no stage is selected (stage=all). Once a stage is picked,
       only the pills whose data-stages list includes the active stage
       are shown ("All topics" always visible). Pills with no constraint
       (data-stages empty) stay visible during the metaobject migration
       window. */
    function applyStageScopeToPills() {
      var topicBar = document.querySelector('.lib-topic-bar');
      if (topicBar) {
        topicBar.classList.toggle('lib-topic-bar--hidden', activeJourney === 'all');
      }

      var stageChanged = false;
      topicPills.forEach(function (pill) {
        var topic = pill.getAttribute('data-filter-topic');
        if (topic === 'all') { pill.style.display = ''; return; }

        var stagesAttr = pill.getAttribute('data-stages') || '';
        var stages = stagesAttr ? stagesAttr.split('|').filter(Boolean) : [];

        var show;
        if (activeJourney === 'all') show = false; // whole row is hidden anyway
        else if (stages.length === 0) show = true; // unconstrained = always show
        else show = stages.indexOf(activeJourney) !== -1;

        pill.style.display = show ? '' : 'none';

        if (!show && pill.classList.contains('is-active')) {
          stageChanged = true;
        }
      });

      if (stageChanged) {
        activeTopic = 'all';
        topicPills.forEach(function (p) {
          p.classList.remove('is-active');
          p.setAttribute('aria-pressed', 'false');
        });
        var allPill = document.querySelector('[data-filter-topic="all"]');
        if (allPill) {
          allPill.classList.add('is-active');
          allPill.setAttribute('aria-pressed', 'true');
        }
      }
    }

    /* Apply stage + topic visibility filters. After card-level filtering,
       any section that ends up with zero visible cards (and isn't the
       general_awareness fallback) collapses entirely — the rail, intro,
       and grid all hide together. A single global empty state surfaces
       when nothing matches across the whole page. */
    function applyFilters() {
      var isAllJourney = activeJourney === 'all';

      // Pass 1: card-level filter (topic only — query is overlay-only)
      document.querySelectorAll('[data-topic]').forEach(function (card) {
        var topicMatch = activeTopic === 'all' || card.getAttribute('data-topic') === activeTopic;
        card.style.display = topicMatch ? '' : 'none';
      });

      // Scope topic pills to the active stage
      applyStageScopeToPills();

      // Pass 2: section visibility. Section shows iff:
      //  - it qualifies under the active stage filter
      //  - AND it has at least one visible card after the card filter
      //    (general_awareness keeps the count check too)
      var anyVisibleSection = false;
      journeySections.forEach(function (sec) {
        var funnel = sec.getAttribute('data-funnel-section');
        var qualifiesByStage;
        if (funnel === 'general_awareness') {
          qualifiesByStage = isAllJourney;
        } else {
          qualifiesByStage = isAllJourney || activeJourney === funnel;
        }

        // Cards inside this section that survived the topic filter
        var visibleCardCount = Array.from(sec.querySelectorAll('.lib-card')).filter(function (c) {
          return c.style.display !== 'none';
        }).length;

        // general_awareness is a collapsed expand-to-see panel — keep it
        // visible (per stage rule) so the toggle can render even with zero
        // cards. Funnel sections collapse when empty.
        var show;
        if (funnel === 'general_awareness') {
          show = qualifiesByStage;
        } else {
          show = qualifiesByStage && visibleCardCount > 0;
        }

        sec.style.display = show ? '' : 'none';
        if (show && funnel !== 'general_awareness' && visibleCardCount > 0) {
          anyVisibleSection = true;
        }
      });

      // Featured rail per stage: hide rail wrapper when no editor's-pick
      // cards are visible. Section intro hides whenever a topic filter is
      // active — the rail becomes the narrowed entry point.
      var topicActive = activeTopic !== 'all';
      document.querySelectorAll('[data-section-rail]').forEach(function (rail) {
        var picks = rail.querySelectorAll('.lib-card');
        var anyVisible = Array.from(picks).some(function (c) {
          return c.style.display !== 'none';
        });
        rail.classList.toggle('lib-section-rail--empty', !anyVisible);
      });
      document.querySelectorAll('[data-section-intro]').forEach(function (intro) {
        intro.setAttribute('data-topic-active', topicActive ? 'true' : 'false');
      });

      // Global empty state — when stage isn't "all" or topic isn't "all"
      // and no funnel section has any visible cards, surface it.
      if (emptyStateEl) {
        var filtersActive = !isAllJourney || topicActive;
        if (filtersActive && !anyVisibleSection) {
          emptyStateEl.removeAttribute('hidden');
        } else {
          emptyStateEl.setAttribute('hidden', '');
        }
      }

      // Per-section fold runs after filter so it operates on the current
      // visible set.
      applyFold();

      updateStatus();
    }

    /* Write current page filter state to URL via replaceState — no reload.
       ?q= is owned by the search overlay, not the page filter. */
    function syncURL() {
      try {
        var url = new URL(window.location.href);
        if (activeJourney === 'all') url.searchParams.delete('stage');
        else url.searchParams.set('stage', activeJourney);

        if (activeTopic === 'all') url.searchParams.delete('topic');
        else url.searchParams.set('topic', activeTopic);

        if (activeSort === 'recommended') url.searchParams.delete('sort');
        else url.searchParams.set('sort', activeSort);

        // Drop legacy alias if present
        url.searchParams.delete('funnel');

        window.history.replaceState({}, '', url);
      } catch (e) {}
    }

    // Stage chip click listeners
    journeyTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        activeJourney = tab.getAttribute('data-journey');
        journeyTabs.forEach(function (t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-pressed', 'false');
        });
        // Activate every chip/picker bound to this journey (desktop chip + mobile picker)
        document.querySelectorAll('[data-journey="' + activeJourney + '"]').forEach(function (t) {
          t.classList.add('is-active');
          t.setAttribute('aria-pressed', 'true');
        });
        applyFilters();
        syncURL();
      });
    });

    // Topic pill click listeners (clicking active pill → deselect → 'all')
    topicPills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        var topic = pill.getAttribute('data-filter-topic');
        activeTopic = (activeTopic === topic && topic !== 'all') ? 'all' : topic;
        topicPills.forEach(function (p) {
          p.classList.remove('is-active');
          p.setAttribute('aria-pressed', 'false');
        });
        var activePill = document.querySelector('[data-filter-topic="' + activeTopic + '"]');
        if (activePill) {
          activePill.classList.add('is-active');
          activePill.setAttribute('aria-pressed', 'true');
        }
        applyFilters();
        syncURL();
      });
    });

    // Sort dropdown
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        activeSort = sortSelect.value || 'recommended';
        applySort();
        applyFold(); // new order → re-evaluate which cards sit in/out of fold
        syncURL();
      });
    }

    // General awareness expand/collapse toggle
    var generalToggle = document.querySelector('[data-general-toggle]');
    var generalGrid   = document.querySelector('[data-general-grid]');
    if (generalToggle && generalGrid) {
      generalToggle.addEventListener('click', function () {
        var expanding = generalGrid.hasAttribute('hidden');
        if (expanding) {
          generalGrid.removeAttribute('hidden');
          generalToggle.classList.add('is-open');
          generalToggle.setAttribute('aria-expanded', 'true');
        } else {
          generalGrid.setAttribute('hidden', '');
          generalToggle.classList.remove('is-open');
          generalToggle.setAttribute('aria-expanded', 'false');
        }
      });
    }

    // Hydrate from URL — MUST run AFTER all listeners are attached.
    // ?funnel= is honoured as a legacy alias for ?stage=. ?q= is owned
    // by the search overlay (initSearchOverlay handles it).
    try {
      var params       = new URLSearchParams(window.location.search);
      var stageParam   = params.get('stage') || params.get('funnel');
      var topicParam   = params.get('topic');
      var sortParam    = params.get('sort');

      if (stageParam) {
        var jMatch = document.querySelector('[data-journey="' + stageParam + '"]');
        if (jMatch) jMatch.click();
      }
      if (topicParam) {
        var tMatch = document.querySelector('[data-filter-topic="' + topicParam + '"]');
        if (tMatch) tMatch.click();
      }
      if (sortParam && sortSelect) {
        sortSelect.value = sortParam;
        activeSort = sortParam;
        applySort();
      }
    } catch (e) {}

    // Initial paint
    applySort();
    applyFilters();
    updateStatus();
    syncURL();
  }

  /* Debounce helper (shared) */
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  /* Escape HTML for safe innerHTML injection */
  function escapeHTML(str) {
    return String(str || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  /* ─────────────────────────────────────────────
   * 1b. SEARCH OVERLAY
   *
   * Trigger button in the chip row opens a focused dialog with its own
   * input + result list. Results come from a JS index built from the
   * already-rendered cards (no network call — Shopify pre-rendered them).
   * Esc, backdrop click, or the × button closes the overlay. URL state:
   * while overlay is open with a query, ?q= is set; closing clears it.
   * Pressing "/" anywhere on the page also opens the overlay.
   * ───────────────────────────────────────────── */
  function initSearchOverlay() {
    var overlay = document.querySelector('[data-search-overlay]');
    var trigger = document.querySelector('[data-search-trigger]');
    var input   = document.querySelector('[data-overlay-input]');
    var list    = document.querySelector('[data-overlay-list]');
    var hintEl  = document.querySelector('[data-overlay-hint]');
    var emptyEl = document.querySelector('[data-overlay-empty]');
    var closes  = document.querySelectorAll('[data-overlay-close]');

    if (!overlay || !input) return;

    // Build a deduped search index from rendered cards (rail + grid
    // render the same article twice; dedupe by URL).
    var seen = {};
    var index = Array.from(document.querySelectorAll('.lib-card'))
      .map(function (card) {
        var titleEl = card.querySelector('.lib-card__title');
        var sumEl   = card.querySelector('.lib-card__summary');
        var chipEl  = card.querySelector('.lib-card__topic-chip');
        var authEl  = card.querySelector('.lib-card__author');
        var dateEl  = card.querySelector('.lib-card__date');
        return {
          url:        card.getAttribute('href'),
          title:      titleEl ? titleEl.textContent.trim() : '',
          summary:    sumEl   ? sumEl.textContent.trim()   : '',
          topicTitle: chipEl  ? chipEl.textContent.trim()  : '',
          topic:      card.getAttribute('data-topic') || '',
          author:     authEl  ? authEl.textContent.trim()  : '',
          date:       dateEl  ? dateEl.textContent.trim()  : '',
          readTime:   card.getAttribute('data-read-time') || ''
        };
      })
      .filter(function (item) {
        if (!item.url || !item.title) return false;
        if (seen[item.url]) return false;
        seen[item.url] = true;
        return true;
      });

    function open(initialValue) {
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (typeof initialValue === 'string') input.value = initialValue;
      // microtask focus so the animation doesn't fight the focus ring
      requestAnimationFrame(function () { input.focus(); input.select(); });
      runQuery();
    }

    function close() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      input.value = '';
      clearOverlayURL();
      runQuery(); // resets the result list to the hint state
    }

    function clearOverlayURL() {
      try {
        var url = new URL(window.location.href);
        url.searchParams.delete('q');
        window.history.replaceState({}, '', url);
      } catch (e) {}
    }

    function syncOverlayURL() {
      try {
        var url = new URL(window.location.href);
        var q = input.value.trim();
        if (q) url.searchParams.set('q', q);
        else url.searchParams.delete('q');
        window.history.replaceState({}, '', url);
      } catch (e) {}
    }

    function renderResults(results) {
      if (results.length === 0) {
        list.innerHTML = '';
        if (emptyEl) emptyEl.removeAttribute('hidden');
        return;
      }
      if (emptyEl) emptyEl.setAttribute('hidden', '');
      list.innerHTML = results.map(function (r) {
        return '<li>' +
          '<a href="' + escapeHTML(r.url) + '" class="lib-overlay__result" data-topic="' + escapeHTML(r.topic) + '" role="option">' +
            '<span class="lib-overlay__result-title">' + escapeHTML(r.title) + '</span>' +
            '<span class="lib-overlay__result-meta">' + escapeHTML(r.date) + (r.readTime ? ' · ' + escapeHTML(r.readTime) + ' min' : '') + '</span>' +
          '</a>' +
        '</li>';
      }).join('');
    }

    function runQuery() {
      var q = input.value.toLowerCase().trim();
      syncOverlayURL();

      if (!q) {
        list.innerHTML = '';
        if (hintEl) hintEl.removeAttribute('hidden');
        if (emptyEl) emptyEl.setAttribute('hidden', '');
        return;
      }
      if (hintEl) hintEl.setAttribute('hidden', '');

      var results = index.filter(function (item) {
        var hay = (item.title + ' ' + item.summary + ' ' + item.topicTitle).toLowerCase();
        return hay.indexOf(q) !== -1;
      }).slice(0, 30);
      renderResults(results);
    }

    // Open triggers
    if (trigger) trigger.addEventListener('click', function () { open(); });

    // Close triggers
    closes.forEach(function (c) { c.addEventListener('click', close); });

    // Esc to close
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
        close();
      }
      // "/" shortcut opens search (when not focused in a field)
      if (e.key === '/' && !overlay.classList.contains('is-open')) {
        var tag = (e.target && e.target.tagName) || '';
        if (tag !== 'INPUT' && tag !== 'TEXTAREA' && !e.target.isContentEditable) {
          e.preventDefault();
          open();
        }
      }
    });

    // Search input debounce
    input.addEventListener('input', debounce(runQuery, 160));
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') e.preventDefault(); // result clicks navigate
    });

    // Hydrate from URL — open overlay if ?q= is set
    try {
      var params = new URLSearchParams(window.location.search);
      var qParam = params.get('q');
      if (qParam) open(qParam);
    } catch (e) {}
  }

  /* ─────────────────────────────────────────────
   * 2. MOBILE JOURNEY PICKER
   * 2×2 tap-card grid visible only on mobile.
   * Syncs with journey tab buttons so applyFilters()
   * doesn't need any changes.
   * ───────────────────────────────────────────── */
  function initMobilePicker() {
    var pickBtns = document.querySelectorAll('[data-pick-journey]');
    if (!pickBtns.length) return;

    function syncPicker(activeJourney) {
      pickBtns.forEach(function (btn) {
        var isActive = btn.getAttribute('data-pick-journey') === activeJourney;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    // Keep picker in sync when a desktop tab is clicked
    document.querySelectorAll('[data-journey]').forEach(function (tab) {
      tab.addEventListener('click', function () {
        syncPicker(tab.getAttribute('data-journey'));
      });
    });

    pickBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var journey = btn.getAttribute('data-pick-journey');
        var isAlreadyActive = btn.classList.contains('is-active');

        if (isAlreadyActive) {
          // Tap active picker → clear to "All"
          var allTab = document.querySelector('[data-journey="all"]');
          if (allTab) allTab.click();
          syncPicker('all');
        } else {
          // Activate this journey via the real tab button
          var matchTab = document.querySelector('[data-journey="' + journey + '"]');
          if (matchTab) matchTab.click();
          syncPicker(journey);

          // Smooth-scroll past the picker into the selected section
          var section = document.querySelector('[data-funnel-section="' + journey + '"]');
          if (section) {
            setTimeout(function () {
              section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 60);
          }
        }
      });
    });
  }

  /* ─────────────────────────────────────────────
   * 3. SECTION ARTICLE COUNTS
   * Counts cards in each journey-section grid and
   * appends a "N guides" badge to the section heading.
   * Only injects on mobile via CSS (badge is styled
   * with display:none on desktop).
   * ───────────────────────────────────────────── */
  function initSectionCounts() {
    document.querySelectorAll('[data-funnel-section]').forEach(function (section) {
      if (section.getAttribute('data-funnel-section') === 'general_awareness') return;

      var grid = section.querySelector('[data-journey-grid]');
      if (!grid) return;

      var count = grid.querySelectorAll('[data-topic]').length;
      if (!count) return;

      var heading = section.querySelector('.lib-section-heading');
      if (!heading) return;

      var badge = document.createElement('span');
      badge.className = 'lib-section-count';
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = count + (count === 1 ? ' guide' : ' guides');
      heading.appendChild(badge);
    });
  }

  /* ─────────────────────────────────────────────
   * 4. TOC GENERATION
   * Scans .article-content h2 elements.
   * Populates .toc-list (sidebar) and .toc-inline-list (mobile).
   * ───────────────────────────────────────────── */
  function slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }

  function initTOC() {
    var content = document.querySelector('.article-content');
    var tocList = document.querySelector('.toc-list');
    var tocInlineList = document.querySelector('.toc-inline-list');

    if (!content) return;

    var headings = content.querySelectorAll('h2, h3');
    if (!headings.length) {
      // Hide TOC containers if no headings
      var tocWrapper = document.querySelector('.art-toc');
      var mobileWrapper = document.querySelector('.art-toc-mobile');
      if (tocWrapper) tocWrapper.style.display = 'none';
      if (mobileWrapper) mobileWrapper.style.display = 'none';
      return;
    }

    headings.forEach(function (heading) {
      // Ensure heading has an id
      if (!heading.id) {
        heading.id = slugify(heading.textContent);
      }
      // Ensure scroll margin clears sticky nav
      heading.style.scrollMarginTop = '80px';

      var linkText = heading.textContent.trim();
      var href = '#' + heading.id;

      // Sidebar TOC
      if (tocList) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = href;
        a.textContent = linkText;
        li.appendChild(a);
        tocList.appendChild(li);
      }

      // Mobile inline TOC
      if (tocInlineList) {
        var liM = document.createElement('li');
        var aM = document.createElement('a');
        aM.href = href;
        aM.textContent = linkText;
        liM.appendChild(aM);
        tocInlineList.appendChild(liM);
      }
    });
  }

  /* ─────────────────────────────────────────────
   * 3. SCROLL SPY
   * IntersectionObserver on H2/H3s — highlights active TOC link.
   * ───────────────────────────────────────────── */
  function initScrollSpy() {
    var content = document.querySelector('.article-content');
    var tocList = document.querySelector('.toc-list');

    if (!content || !tocList) return;

    var headings = content.querySelectorAll('h2[id], h3[id]');
    if (!headings.length) return;

    var activeId = null;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            activeId = entry.target.id;
            updateActiveLink(activeId);
          }
        });
      },
      {
        rootMargin: '-80px 0px -60% 0px',
        threshold: 0
      }
    );

    headings.forEach(function (h) { observer.observe(h); });

    function updateActiveLink(id) {
      var links = tocList.querySelectorAll('a');
      links.forEach(function (link) {
        if (link.getAttribute('href') === '#' + id) {
          link.classList.add('is-active');
        } else {
          link.classList.remove('is-active');
        }
      });
    }
  }

  /* ─────────────────────────────────────────────
   * 4. GLOSSARY SCANNING
   * Reads #glossary-data JSON. Wraps first match per term
   * in .article-content with <span class="glossary-term">.
   * Skips headings, links, already-wrapped terms.
   * ───────────────────────────────────────────── */
  function initGlossary() {
    var dataEl = document.getElementById('glossary-data');
    var content = document.querySelector('.article-content');

    if (!dataEl || !content) return;

    var terms;
    try {
      terms = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    if (!terms || !terms.length) return;

    // Track which terms have already been wrapped
    var wrapped = {};

    terms.forEach(function (termObj) {
      if (!termObj.term) return;

      // Build list of all variants to match (term + aliases)
      var variants = [termObj.term];
      if (Array.isArray(termObj.aliases)) {
        termObj.aliases.forEach(function (alias) {
          if (alias && alias.trim()) variants.push(alias.trim());
        });
      }

      // Try each variant until first match
      for (var vi = 0; vi < variants.length; vi++) {
        var variant = variants[vi];
        if (wrapped[termObj.term]) break;

        var escaped = variant.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        var regex = new RegExp('\\b' + escaped + '\\b', 'i');

        wrapFirstMatch(content, regex, termObj);
      }
    });
  }

  function wrapFirstMatch(root, regex, termObj) {
    // Walk text nodes, skip headings/links/already-wrapped/interactive
    var skipTags = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'A', 'BUTTON', 'SCRIPT', 'STYLE', 'CODE', 'PRE']);
    var skipClasses = ['glossary-term'];

    var walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function (node) {
          var parent = node.parentNode;
          // Skip if ancestor is in skipTags
          var el = parent;
          while (el && el !== root) {
            if (skipTags.has(el.tagName)) return NodeFilter.FILTER_REJECT;
            if (el.classList) {
              for (var i = 0; i < skipClasses.length; i++) {
                if (el.classList.contains(skipClasses[i])) return NodeFilter.FILTER_REJECT;
              }
            }
            el = el.parentNode;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    var node;
    while ((node = walker.nextNode())) {
      var text = node.textContent;
      var match = regex.exec(text);
      if (!match) continue;

      // Split and wrap
      var before = text.slice(0, match.index);
      var matched = match[0];
      var after = text.slice(match.index + matched.length);

      var span = document.createElement('span');
      span.className = 'glossary-term';
      span.setAttribute('tabindex', '0');
      span.setAttribute('data-term', termObj.term);

      // Tooltip
      var tooltip = document.createElement('span');
      tooltip.className = 'glossary-tooltip';
      tooltip.setAttribute('role', 'tooltip');

      // Term title
      var titleEl = document.createElement('span');
      titleEl.className = 'glossary-tooltip__title';
      titleEl.textContent = termObj.term;
      tooltip.appendChild(titleEl);

      // Definition body
      var bodyEl = document.createElement('span');
      bodyEl.className = 'glossary-tooltip__body';
      bodyEl.textContent = termObj.definition || '';
      tooltip.appendChild(bodyEl);

      // "Read more" link (only when a related article URL exists)
      if (termObj.relatedUrl) {
        var readMore = document.createElement('a');
        readMore.href = termObj.relatedUrl;
        readMore.className = 'glossary-tooltip__read-more';
        readMore.textContent = 'Read more \u2192';
        tooltip.appendChild(readMore);
      }

      span.textContent = matched;
      span.appendChild(tooltip);

      var frag = document.createDocumentFragment();
      if (before) frag.appendChild(document.createTextNode(before));
      frag.appendChild(span);
      if (after) frag.appendChild(document.createTextNode(after));

      node.parentNode.replaceChild(frag, node);

      // Mobile tap to toggle
      span.addEventListener('click', function (e) {
        // Allow tooltip links to work
        if (e.target.tagName === 'A') return;
        var isOpen = this.classList.contains('tooltip-open');
        // Close all others
        document.querySelectorAll('.glossary-term.tooltip-open').forEach(function (el) {
          el.classList.remove('tooltip-open');
        });
        if (!isOpen) this.classList.add('tooltip-open');
        e.stopPropagation();
      });

      // Mark as wrapped and stop looking for this term
      return true;
    }
    return false;
  }

  /* ─────────────────────────────────────────────
   * 5. COPY LINK
   * Copies window.location.href to clipboard.
   * Shows "Copied!" message briefly.
   * ───────────────────────────────────────────── */
  function initCopyLink() {
    var buttons = document.querySelectorAll('[data-copy-link]');
    var msgs = document.querySelectorAll('[data-copied-msg]');

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(window.location.href).then(function () {
            showCopied();
          }).catch(function () {
            fallbackCopy();
          });
        } else {
          fallbackCopy();
        }
      });
    });

    function showCopied() {
      msgs.forEach(function (msg) {
        msg.style.display = 'block';
        setTimeout(function () { msg.style.display = ''; }, 2000);
      });
    }

    function fallbackCopy() {
      var ta = document.createElement('textarea');
      ta.value = window.location.href;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); showCopied(); } catch (e) {}
      document.body.removeChild(ta);
    }
  }

  /* ─────────────────────────────────────────────
   * 6. CHANGELOG TOGGLE
   * Toggles .open class on .changelog-toggle and sibling content.
   * ───────────────────────────────────────────── */
  function initChangelog() {
    var toggles = document.querySelectorAll('.changelog-toggle');

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var isOpen = toggle.classList.toggle('open');
        var content = toggle.nextElementSibling;
        if (content) {
          content.classList.toggle('open', isOpen);
        }
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    });
  }

  /* ─────────────────────────────────────────────
   * 7. MOBILE TOC TOGGLE
   * Toggles .is-open on button and sibling list.
   * Clicking a link inside the list also closes it.
   * ───────────────────────────────────────────── */
  function initMobileTOC() {
    var toggle = document.querySelector('.toc-inline-toggle');
    var list = document.querySelector('.toc-inline-list');

    if (!toggle || !list) return;

    function closeTOC() {
      toggle.classList.remove('is-open');
      list.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      list.setAttribute('aria-hidden', 'true');
    }

    toggle.addEventListener('click', function () {
      var isOpen = toggle.classList.toggle('is-open');
      list.classList.toggle('is-open', isOpen);
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      list.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    });

    // Close when user taps a link (page will jump to anchor)
    list.addEventListener('click', function (e) {
      if (e.target.tagName === 'A') closeTOC();
    });
  }

  /* ─────────────────────────────────────────────
   * 8. CLOSE GLOSSARY TOOLTIPS ON OUTSIDE CLICK
   * ───────────────────────────────────────────── */
  function initGlossaryDismiss() {
    document.addEventListener('click', function () {
      document.querySelectorAll('.glossary-term.tooltip-open').forEach(function (el) {
        el.classList.remove('tooltip-open');
      });
    });
  }

  /* ─────────────────────────────────────────────
   * 9. GLOSSARY TOOLTIP VIEWPORT CLAMPING
   * Repositions tooltips that would overflow left/right edge.
   * Uses event delegation on .article-content.
   * ───────────────────────────────────────────── */
  function initTooltipPositioning() {
    var content = document.querySelector('.article-content');
    if (!content) return;

    function clampTooltip(term) {
      var tooltip = term.querySelector('.glossary-tooltip');
      if (!tooltip) return;

      // Reset to centered default first
      tooltip.style.left = '50%';
      tooltip.style.right = 'auto';
      tooltip.style.transform = 'translateX(-50%)';

      // Measure after brief layout pass
      requestAnimationFrame(function () {
        var rect = tooltip.getBoundingClientRect();
        var vw = window.innerWidth || document.documentElement.clientWidth;
        var pad = 12; // px gap from viewport edge

        if (rect.left < pad) {
          // Too far left — anchor to left edge of term
          tooltip.style.left = '0';
          tooltip.style.transform = 'none';
        } else if (rect.right > vw - pad) {
          // Too far right — anchor to right edge of term
          tooltip.style.left = 'auto';
          tooltip.style.right = '0';
          tooltip.style.transform = 'none';
        }
      });
    }

    content.addEventListener('mouseenter', function (e) {
      var term = e.target.closest('.glossary-term');
      if (term) clampTooltip(term);
    }, true);

    content.addEventListener('focusin', function (e) {
      var term = e.target.closest('.glossary-term');
      if (term) clampTooltip(term);
    });

    // Also clamp on mobile tap (tooltip-open class added by click handler)
    content.addEventListener('click', function (e) {
      var term = e.target.closest('.glossary-term');
      if (term) setTimeout(function () { clampTooltip(term); }, 0);
    });
  }

  /* ─────────────────────────────────────────────
   * 10. CONTENT SANITIZER
   * Runs on .article-content after DOM is ready.
   * - Removes [[shortcode]] patterns
   * - Strips empty paragraphs and lone <br> paragraphs
   * - Unwraps <strong>/<b>/<span> inside headings
   * - Removes editor-injected inline styles (except our own)
   * ───────────────────────────────────────────── */
  function initContentSanitizer() {
    var content = document.querySelector('.article-content');
    if (!content) return;

    // 1. Remove [[shortcode]] patterns from text nodes
    var SHORTCODE_RE = /\[\[[^\]]*\]\]/g;
    var walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT, null);
    var textNodes = [];
    var node;
    while ((node = walker.nextNode())) {
      if (SHORTCODE_RE.test(node.textContent)) textNodes.push(node);
      SHORTCODE_RE.lastIndex = 0;
    }
    textNodes.forEach(function (n) {
      n.textContent = n.textContent.replace(SHORTCODE_RE, '');
    });

    // Also remove any block-level element that is now entirely empty
    // (e.g. a <p> that contained only a shortcode)
    content.querySelectorAll('p, div').forEach(function (el) {
      if (el.textContent.trim() === '' && el.children.length === 0) el.remove();
    });

    // 2. Remove empty paragraphs (&nbsp;-only or pure whitespace)
    content.querySelectorAll('p').forEach(function (p) {
      var t = p.innerHTML.trim();
      if (t === '' || t === '&nbsp;' || t === '<br>' || t === '<br/>') p.remove();
    });

    // 3. Collapse runs of more than 2 consecutive <br> elements
    //    (replace any 3+ <br> sequence with a single paragraph break)
    var brRuns = content.querySelectorAll('br + br + br');
    brRuns.forEach(function (br) {
      // Walk back and remove excess BRs
      var prev = br.previousSibling;
      while (prev && prev.nodeName === 'BR') {
        var tmp = prev.previousSibling;
        prev.remove();
        prev = tmp;
      }
      br.remove();
    });

    // 4. Unwrap inline formatting tags inside headings
    //    Keeps text, removes the wrapper (prevents double-bold, odd font sizes)
    content.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach(function (h) {
      h.querySelectorAll('strong, b, em, i, u, span').forEach(function (el) {
        var frag = document.createDocumentFragment();
        while (el.firstChild) frag.appendChild(el.firstChild);
        el.parentNode.replaceChild(frag, el);
      });
    });

    // 5. Strip inline style attributes injected by the editor
    //    Preserve our own utility styles (glossary display:none, scroll margin)
    var OWN_CLASSES = ['glossary-term', 'glossary-tooltip', 'article-content', 'art-body'];
    content.querySelectorAll('[style]').forEach(function (el) {
      var hasOwnClass = OWN_CLASSES.some(function (c) { return el.classList.contains(c); });
      if (!hasOwnClass) el.removeAttribute('style');
    });
  }

  /* ─────────────────────────────────────────────
   * 11. SHELF SCROLL NAV
   * Prev/next buttons for the mobile card carousels.
   * Buttons are CSS-hidden on desktop; this wires their
   * click → scrollBy(one card width) and updates disabled
   * state via a passive scroll listener on each grid.
   * ───────────────────────────────────────────── */
  function initShelfScrollNav() {
    var navs = document.querySelectorAll('.lib-grid-nav');
    if (!navs.length) return;

    navs.forEach(function (nav) {
      var section = nav.closest('.lib-journey-section');
      if (!section) return;
      var grid = section.querySelector('.lib-section-grid');
      if (!grid) return;

      var prevBtn = nav.querySelector('[data-grid-prev]');
      var nextBtn = nav.querySelector('[data-grid-next]');
      if (!prevBtn || !nextBtn) return;

      function cardScrollAmount() {
        var card = grid.querySelector('[data-topic]');
        if (!card) return grid.clientWidth * 0.8;
        var gapPx = parseFloat(getComputedStyle(grid).gap) || 12;
        return card.offsetWidth + gapPx;
      }

      function updateButtons() {
        var sl = Math.round(grid.scrollLeft);
        var maxScroll = Math.round(grid.scrollWidth - grid.clientWidth);
        prevBtn.disabled = sl <= 1;
        nextBtn.disabled = sl >= maxScroll - 1;
      }

      prevBtn.addEventListener('click', function () {
        grid.scrollBy({ left: -cardScrollAmount(), behavior: 'smooth' });
      });

      nextBtn.addEventListener('click', function () {
        grid.scrollBy({ left: cardScrollAmount(), behavior: 'smooth' });
      });

      grid.addEventListener('scroll', updateButtons, { passive: true });
      updateButtons();
    });
  }

  /* ─────────────────────────────────────────────
   * INIT
   * ───────────────────────────────────────────── */
  function init() {
    initContentSanitizer(); // run first so glossary scans clean content
    initTopicFilter();
    initSearchOverlay();
    initMobilePicker();
    initSectionCounts();
    initShelfScrollNav();
    initTOC();
    initScrollSpy();
    initGlossary();
    initCopyLink();
    initChangelog();
    initMobileTOC();
    initGlossaryDismiss();
    initTooltipPositioning();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
